import type { ElementType, ReactNode } from 'react';
import { Search } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useControlled } from '../../lib/use-controlled';
import { CountBadge } from '../ui/count-badge';
import { Icon, type LucideIcon } from '../ui/icon';
import { Input } from '../ui/input';
import { Kbd } from '../ui/kbd';
import { Logo } from './logo';

export interface NavItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  count?: number;
  kbd?: string;
  href?: string;
}

export interface NavSection {
  /** Nach Arbeitsweise: „Vertrieb“, „Arbeit“, „Auswertung“. Höchstens vier Gruppen. */
  label?: string;
  items: NavItem[];
}

export interface SidebarProps {
  /** Kürzel als Pill: „CRM“, „PM“. */
  app?: string;
  /** Ersetzt die Wortmarke. */
  header?: ReactNode;
  search?: { placeholder?: string; kbd?: string; value?: string; onChange?: (value: string) => void } | false;
  sections: NavSection[];
  /** Hilfe und Einstellungen. */
  footer?: NavItem[];
  activeId?: string;
  onSelect?: (id: string) => void;
  /**
   * Link-Komponente für Einträge mit `href`, z. B. ein Adapter auf react-router:
   * `({ href, ...p }) => <Link to={href} {...p} />`. Standard: <a>.
   */
  linkAs?: ElementType;
  className?: string;
}

/** Das schwebende App-Panel: Wortmarke und App-Name, Suche, gruppierte Navigation, Fußzeile. */
export function Sidebar({
  app,
  header,
  search = { placeholder: 'Suchen', kbd: '⌘K' },
  sections,
  footer,
  activeId,
  onSelect,
  linkAs: LinkAs = 'a',
  className,
}: SidebarProps) {
  const [aktiv, setAktiv] = useControlled(activeId, sections[0]?.items[0]?.id ?? '', onSelect);

  const eintrag = (it: NavItem) => {
    const istAktiv = aktiv === it.id;
    const Comp: ElementType = it.href ? LinkAs : 'button';
    return (
      <li key={it.id}>
        <Comp
          {...(it.href ? { href: it.href } : { type: 'button' })}
          aria-current={istAktiv ? 'page' : undefined}
          onClick={() => setAktiv(it.id)}
          className={cn(
            'group flex h-control-md w-full cursor-pointer items-center gap-2.5 rounded-md pl-2.5 pr-2 text-left text-sm text-sidebar-foreground no-underline transition-colors duration-[120ms]',
            'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            'aria-[current=page]:bg-sidebar-primary aria-[current=page]:font-medium aria-[current=page]:text-sidebar-primary-foreground',
          )}
        >
          {it.icon && <Icon icon={it.icon} size={17} className="text-muted-foreground group-aria-[current=page]:text-inherit" />}
          <span className="min-w-0 flex-1 truncate">{it.label}</span>
          {it.count != null && <CountBadge cap={false} className="group-aria-[current=page]:bg-transparent group-aria-[current=page]:text-inherit">{it.count}</CountBadge>}
          {it.kbd && <Kbd>{it.kbd}</Kbd>}
        </Comp>
      </li>
    );
  };

  return (
    <aside
      className={cn(
        'flex h-full w-sidebar flex-col rounded-xl border border-sidebar-border bg-sidebar p-3 text-sidebar-foreground',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2 pb-3.5 pl-2.5 pr-2 pt-1.5">
        {header ?? <Logo variant="wordmark" height={19} />}
        {app && <span className="rounded-full bg-muted px-2 py-0.5 t-label text-muted-foreground">{app}</span>}
      </div>
      {search && (
        <Input
          icon={Search}
          size="sm"
          placeholder={search.placeholder ?? 'Suchen'}
          aria-label={search.placeholder ?? 'Suchen'}
          kbd={search.kbd}
          value={search.value}
          onChange={search.onChange ? (e) => search.onChange?.(e.target.value) : undefined}
          className="mb-4"
        />
      )}
      <nav aria-label="Navigation" className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
        {sections.map((s, i) => (
          <div key={s.label ?? i}>
            {s.label && <div className="px-2.5 pb-1.5 t-label text-muted-foreground">{s.label}</div>}
            <ul className="flex flex-col gap-0.5">{s.items.map(eintrag)}</ul>
          </div>
        ))}
      </nav>
      {footer && footer.length > 0 && <ul className="mt-3 flex flex-col gap-0.5 border-t border-sidebar-border pt-3">{footer.map(eintrag)}</ul>}
    </aside>
  );
}
